// Tableau de bord des responsables : stock livrable, production, livraisons, points à surveiller.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Card, CardContent, Chip, Grid, IconButton, Skeleton, Tooltip, Typography,
} from '@mui/material';
import {
  RefreshOutlined, Inventory2Outlined, HourglassBottomOutlined, FactoryOutlined, LocalShippingOutlined,
  ErrorOutline, WarningAmberOutlined, CheckCircleOutline, AssignmentOutlined, ChevronRight, ConstructionOutlined,
} from '@mui/icons-material';
import {
  Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../services/api';
import { apiError, BL_STATUS } from '../utils/format';
import { tokens } from '../theme';

// Couleurs des séries (validées : séparation daltonisme et lisibilité) — identité portée aussi par la légende
const C_PROD = '#3566A8';
const C_LIV = '#C9850F';
const n = (v) => Number(v || 0).toLocaleString('fr-FR');
const dmy = (d) => { const [y, m, j] = String(d).slice(0, 10).split('-'); return `${j}/${m}/${y}`; };
const dm = (d) => { const [, m, j] = String(d).slice(0, 10).split('-'); return `${j}/${m}`; };
const longDate = (d) => new Date(`${d}T00:00:00Z`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });

const Kpi = ({ icon: Icon, label, value, sub, accent, onClick }) => (
  <Card variant="outlined" sx={{ height: '100%', cursor: onClick ? 'pointer' : 'default', transition: 'border-color .15s', '&:hover': onClick ? { borderColor: tokens.navy } : {} }} onClick={onClick}>
    <CardContent sx={{ p: 2.25, '&:last-child': { pb: 2.25 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
        <Box sx={{ width: 30, height: 30, borderRadius: 1.5, display: 'grid', placeItems: 'center', bgcolor: accent || '#EEF1F6', color: tokens.navy }}>
          <Icon sx={{ fontSize: 18 }} />
        </Box>
        <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{label}</Typography>
      </Box>
      <Typography sx={{ fontFamily: tokens.mono, fontSize: 34, fontWeight: 600, lineHeight: 1.15, mt: 1.25, color: tokens.ink }}>{value}</Typography>
      <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>{sub}</Typography>
    </CardContent>
  </Card>
);

const CardTitle = ({ children, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
    <Typography sx={{ fontWeight: 600, fontSize: 16 }}>{children}</Typography>
    {action}
  </Box>
);

const LinkBtn = ({ children, onClick }) => (
  <Box component="button" onClick={onClick}
    sx={{ all: 'unset', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'primary.main', display: 'inline-flex', alignItems: 'center', '&:hover': { textDecoration: 'underline' } }}>
    {children}<ChevronRight sx={{ fontSize: 18 }} />
  </Box>
);

function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <Box sx={{ bgcolor: '#fff', border: 1, borderColor: 'divider', borderRadius: 1.5, px: 1.5, py: 1, boxShadow: '0 4px 14px rgba(18,40,76,.12)', fontSize: 13 }}>
      <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 0.5, textTransform: 'capitalize' }}>{longDate(label)}</Typography>
      {[['Production', p.production, C_PROD], ['Livraisons', p.livraisons, C_LIV]].map(([l, v, c]) => (
        <Box key={l} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: c }} />
          <span style={{ flex: 1 }}>{l}</span><b style={{ fontFamily: tokens.mono }}>{n(v)}</b>
        </Box>
      ))}
      {p.sorties > 0 && <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>Sorties internes et avaries : {n(p.sorties)}</Typography>}
    </Box>
  );
}

const Watch = ({ icon: Icon, color, title, detail, onClick }) => (
  <Box onClick={onClick} sx={{ display: 'flex', gap: 1.25, py: 1.1, borderTop: 1, borderColor: 'divider', cursor: onClick ? 'pointer' : 'default', '&:first-of-type': { borderTop: 0, pt: 0 } }}>
    <Icon sx={{ fontSize: 20, color, mt: '1px' }} />
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{title}</Typography>
      {detail && <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{detail}</Typography>}
    </Box>
  </Box>
);

const STATE = (t) => (t.disponible === 0 ? (t.total > 0 ? ['En maturation', 'info'] : ['Rupture', 'error']) : t.disponible < 10 ? ['Faible', 'warning'] : ['Normal', 'success']);

const Dashboard = () => {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [controle, setControle] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [r, c] = await Promise.all([api.get('/stock/tableau-de-bord'), api.get('/stock/controle').catch(() => null)]);
      setD(r.data); setControle(c?.data || null);
    } catch (e) { setError(apiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const actions = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {d && <Typography sx={{ color: 'text.secondary', fontSize: 14, textTransform: 'capitalize' }}>{longDate(d.date)}</Typography>}
      <Tooltip title="Actualiser"><span><IconButton onClick={load} disabled={loading} aria-label="Actualiser"><RefreshOutlined /></IconButton></span></Tooltip>
    </Box>
  );

  if (!d) {
    return (
      <Layout title="Tableau de bord" actions={actions} plain>
        {error ? <Alert severity="error">{error}</Alert> : (
          <Grid container spacing={2}>{[0, 1, 2, 3].map((i) => <Grid item xs={6} md={3} key={i}><Skeleton variant="rounded" height={130} /></Grid>)}</Grid>
        )}
      </Layout>
    );
  }

  const t = d.totaux;
  const ruptures = d.types.filter((x) => x.total === 0);
  const faibles = d.types.filter((x) => x.disponible > 0 && x.disponible < 10);
  const attente = d.types.filter((x) => x.disponible === 0 && x.total > 0);
  const totProd = d.serie.reduce((s, x) => s + x.production, 0);
  const totLiv = d.serie.reduce((s, x) => s + x.livraisons, 0);
  const maxType = Math.max(1, ...d.types.map((x) => x.total));
  const types = [...d.types].sort((a, b) => b.total - a.total || a.code.localeCompare(b.code));

  const watch = [];
  if (controle && !controle.coherent) {
    watch.push({ icon: ErrorOutline, color: 'error.main', title: 'Écart de stock détecté', detail: controle.ecarts.map((e) => `${e.code} : lots ${e.stock_lots}, journal ${e.stock_journal}`).join(' · '), onClick: () => navigate('/physical-inventory') });
  }
  if (d.bl.a_valider) watch.push({ icon: LocalShippingOutlined, color: 'warning.main', title: `${d.bl.a_valider} bon(s) de livraison à valider`, onClick: () => navigate('/bons-livraison') });
  if (ruptures.length) watch.push({ icon: ErrorOutline, color: 'error.main', title: `${ruptures.length} type(s) en rupture`, detail: ruptures.map((x) => x.code).join(', '), onClick: () => navigate('/stock') });
  if (faibles.length) watch.push({ icon: WarningAmberOutlined, color: 'warning.main', title: `Stock livrable faible`, detail: faibles.map((x) => `${x.code} (${x.disponible})`).join(', '), onClick: () => navigate('/stock') });
  if (attente.length) watch.push({ icon: HourglassBottomOutlined, color: 'info.main', title: 'Rien de livrable pour l’instant', detail: attente.map((x) => `${x.code} (${x.total} en maturation)`).join(', '), onClick: () => navigate('/pba-maturation') });
  if (d.intrants_alertes.length) {
    watch.push({ icon: ConstructionOutlined, color: 'warning.main', title: `${d.intrants_alertes.length} intrant(s) à réapprovisionner`, detail: d.intrants_alertes.slice(0, 4).map((i) => `${i.designation} (${n(i.stock)} ${i.unite})`).join(', ') + (d.intrants_alertes.length > 4 ? '…' : ''), onClick: () => navigate('/stock-intrants') });
  }

  return (
    <Layout title="Tableau de bord" actions={actions} plain>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} lg={3}>
          <Kpi icon={Inventory2Outlined} label="Stock livrable" value={n(t.disponible)} sub={`sur ${n(t.total)} poteaux en stock`} accent="#E3ECF7" onClick={() => navigate('/stock')} />
        </Grid>
        <Grid item xs={6} lg={3}>
          <Kpi icon={HourglassBottomOutlined} label="En maturation" value={n(t.maturation)} sub={t.bientot ? `${n(t.bientot)} livrables d’ici 2 jours` : 'aucun livrable sous 2 jours'} onClick={() => navigate('/pba-maturation')} />
        </Grid>
        <Grid item xs={6} lg={3}>
          <Kpi icon={FactoryOutlined} label="Produits aujourd’hui" value={n(d.jour.production)} sub={`moyenne ${n(d.moyenne_production)} / jour sur ${d.serie.length - 1} jours`} onClick={() => navigate('/reports-history')} />
        </Grid>
        <Grid item xs={6} lg={3}>
          <Kpi icon={LocalShippingOutlined} label="Livrés aujourd’hui" value={n(d.jour.livraisons)} sub={`${d.bl.du_jour} BL du jour${d.bl.a_valider ? ` · ${d.bl.a_valider} à valider` : ''}`} accent="#FBF0DA" onClick={() => navigate('/bons-livraison')} />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} lg={8}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <CardTitle action={
                <Box sx={{ display: 'flex', gap: 2, fontSize: 13 }}>
                  {[['Production', totProd, C_PROD], ['Livraisons', totLiv, C_LIV]].map(([l, v, c]) => (
                    <Box key={l} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: c }} />
                      <span>{l}</span><b style={{ fontFamily: tokens.mono }}>{n(v)}</b>
                    </Box>
                  ))}
                </Box>
              }>
                Production et livraisons — {d.serie.length} derniers jours
              </CardTitle>
              <Box sx={{ height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={d.serie} barGap={2} barCategoryGap="22%" margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="#E8EBEF" />
                    <XAxis dataKey="date" tickFormatter={dm} tick={{ fontSize: 11.5, fill: '#5B6675' }} axisLine={{ stroke: '#D9DDE3' }} tickLine={false} interval="preserveStartEnd" minTickGap={8} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11.5, fill: '#5B6675' }} axisLine={false} tickLine={false} />
                    <ChartTooltip content={<ChartTip />} cursor={{ fill: 'rgba(18,40,76,.05)' }} />
                    <Bar dataKey="production" name="Production" fill={C_PROD} radius={[4, 4, 0, 0]} maxBarSize={18} isAnimationActive={false} />
                    <Bar dataKey="livraisons" name="Livraisons" fill={C_LIV} radius={[4, 4, 0, 0]} maxBarSize={18} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <CardTitle>À surveiller</CardTitle>
              {watch.length === 0 ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'success.main', py: 2 }}>
                  <CheckCircleOutline /> <Typography>Rien à signaler</Typography>
                </Box>
              ) : watch.map((w) => <Watch key={w.title} {...w} />)}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <Card variant="outlined">
            <CardContent>
              <CardTitle action={<LinkBtn onClick={() => navigate('/stock')}>Gérer le stock</LinkBtn>}>Stock par type</CardTitle>
              <Box sx={{ overflowX: 'auto' }}>
              <Box component="table" sx={{
                width: '100%', minWidth: 420, borderCollapse: 'collapse', fontSize: 13.5,
                '& .repart': { display: { xs: 'none', sm: 'table-cell' } },
                '& th': { fontSize: 12, fontWeight: 600, color: 'text.secondary', textAlign: 'right', pb: 1, borderBottom: 1, borderColor: 'divider' },
                '& td': { py: 0.9, borderBottom: 1, borderColor: 'divider', textAlign: 'right', fontFamily: tokens.mono },
                '& tr:last-of-type td': { borderBottom: 0 },
              }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Type</th>
                    <th className="repart" style={{ textAlign: 'left', paddingLeft: 12 }}>Répartition</th>
                    <th>Livrable</th><th>Maturation</th><th>Total</th>
                    <th style={{ textAlign: 'left', paddingLeft: 12 }}>État</th>
                  </tr>
                </thead>
                <tbody>
                  {types.map((x) => {
                    const [label, color] = STATE(x);
                    return (
                      <tr key={x.code}>
                        <td style={{ textAlign: 'left', fontFamily: tokens.sans, fontWeight: 600 }}>{x.code}</td>
                        <td className="repart" style={{ width: '28%', paddingLeft: 12 }}>
                          <Tooltip title={`${x.disponible} livrables · ${x.maturation} en maturation`}>
                            <Box sx={{ display: 'flex', gap: '2px', height: 8, width: `${(x.total / maxType) * 100}%`, minWidth: x.total ? 6 : 0 }}>
                              {x.disponible > 0 && <Box sx={{ flex: x.disponible, bgcolor: tokens.navy, borderRadius: 1 }} />}
                              {x.maturation > 0 && <Box sx={{ flex: x.maturation, bgcolor: '#A9BCD8', borderRadius: 1 }} />}
                            </Box>
                          </Tooltip>
                        </td>
                        <td style={{ fontWeight: 700 }}>{x.disponible}</td>
                        <td style={{ color: '#5B6675' }}>{x.maturation}</td>
                        <td>{x.total}</td>
                        <td style={{ textAlign: 'left', paddingLeft: 12, fontFamily: tokens.sans }}>
                          <Chip size="small" label={label} color={color} variant={color === 'success' ? 'outlined' : 'filled'} sx={{ height: 22, fontSize: 12 }} />
                        </td>
                      </tr>
                    );
                  })}
                  <tr>
                    <td style={{ textAlign: 'left', fontFamily: tokens.sans, fontWeight: 700, borderTop: '2px solid #D9DDE3' }}>Total</td>
                    <td className="repart" style={{ borderTop: '2px solid #D9DDE3' }} />
                    <td style={{ fontWeight: 700, borderTop: '2px solid #D9DDE3' }}>{t.disponible}</td>
                    <td style={{ fontWeight: 700, borderTop: '2px solid #D9DDE3' }}>{t.maturation}</td>
                    <td style={{ fontWeight: 700, borderTop: '2px solid #D9DDE3' }}>{t.total}</td>
                    <td style={{ borderTop: '2px solid #D9DDE3' }} />
                  </tr>
                </tbody>
              </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1.5, fontSize: 12, color: 'text.secondary', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}><Box sx={{ width: 14, height: 8, bgcolor: tokens.navy, borderRadius: 1 }} />Livrable (mature)</Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}><Box sx={{ width: 14, height: 8, bgcolor: '#A9BCD8', borderRadius: 1 }} />En maturation</Box>
                {controle && (controle.coherent
                  ? <Box sx={{ ml: 'auto', color: 'success.main', display: 'flex', alignItems: 'center', gap: 0.5 }}><CheckCircleOutline sx={{ fontSize: 16 }} />Stock contrôlé : lots = journal des mouvements</Box>
                  : <Box sx={{ ml: 'auto', color: 'error.main', display: 'flex', alignItems: 'center', gap: 0.5 }}><ErrorOutline sx={{ fontSize: 16 }} />Écart lots / journal</Box>)}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <CardTitle action={<LinkBtn onClick={() => navigate('/reports-history')}>Tous les rapports</LinkBtn>}>Derniers rapports</CardTitle>
              {d.rapports.length === 0 && <Typography color="text.secondary">Aucun rapport</Typography>}
              {d.rapports.map((r) => (
                <Box key={r.id} onClick={() => navigate('/reports-history')} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderTop: 1, borderColor: 'divider', cursor: 'pointer', '&:first-of-type': { borderTop: 0 }, '&:hover': { bgcolor: '#F7F8FA' } }}>
                  <AssignmentOutlined sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{dmy(r.date)}</Typography>
                    <Typography noWrap sx={{ fontSize: 12.5, color: 'text.secondary' }}>{r.auteur}</Typography>
                  </Box>
                  <Typography sx={{ fontFamily: tokens.mono, fontWeight: 600 }}>{n(r.total_pba)} <span style={{ fontSize: 12, color: '#5B6675', fontFamily: tokens.sans, fontWeight: 400 }}>PBA</span></Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
          <Card variant="outlined">
            <CardContent>
              <CardTitle action={<LinkBtn onClick={() => navigate('/bons-livraison')}>Tous les BL</LinkBtn>}>Dernières livraisons</CardTitle>
              {d.livraisons.length === 0 && <Typography color="text.secondary">Aucune livraison</Typography>}
              {d.livraisons.map((l) => (
                <Box key={l.id} onClick={() => navigate(`/bons-livraison/${l.id}`)} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderTop: 1, borderColor: 'divider', cursor: 'pointer', '&:first-of-type': { borderTop: 0 }, '&:hover': { bgcolor: '#F7F8FA' } }}>
                  <LocalShippingOutlined sx={{ fontSize: 20, color: 'text.secondary' }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600, fontFamily: tokens.mono }}>{l.bl_numero}</Typography>
                    <Typography noWrap sx={{ fontSize: 12.5, color: 'text.secondary' }}>{dmy(l.date)} · {l.client_name}</Typography>
                  </Box>
                  <Chip size="small" label={BL_STATUS[l.status]?.label || l.status} color={BL_STATUS[l.status]?.color || 'default'} variant="outlined" sx={{ height: 22, fontSize: 12 }} />
                  <Typography sx={{ fontFamily: tokens.mono, fontWeight: 600, minWidth: 34, textAlign: 'right' }}>{n(l.quantite)}</Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Layout>
  );
};

export default Dashboard;
